import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { MdPsychology, MdTranslate, MdArrowForward, MdBookmark, MdArticle } from 'react-icons/md';
import './OverviewPage.css';

const ACTION_CARDS = [
  {
    key: 'scanner',
    route: '/scanner',
    icon: <MdPsychology />,
    iconClass: 'icon-en',
    label: 'English Scanner',
    delay: '0.10s',
    description: 'Analyze your current emotional frequency and receive curated English news matched to your mood.',
    cta: 'Start Scanning',
    tag: 'Real-time',
    tagClass: 'tag-primary',
    features: ['Face-API emotion detection', '9 category filters', 'Bookmark articles'],
  },
  {
    key: 'scanner-tamil',
    route: '/scanner-tamil',
    icon: <MdTranslate />,
    iconClass: 'icon-ta',
    label: 'Tamil Scanner',
    delay: '0.18s',
    description: 'உங்கள் மனநிலையை பகுப்பாய்வு செய்து, உங்களுக்கேற்ற தமிழ் செய்திகளைப் பெறுங்கள்.',
    cta: 'ஸ்கேன் தொடங்கவும்',
    tag: 'தமிழ்',
    tagClass: 'tag-success',
    features: ['வகைப்படுத்தல்', 'நேரடி பகுப்பாய்வு', 'சேமிப்பு'],
  },
  {
    key: 'bookmarks',
    route: '/bookmarks',
    icon: <MdBookmark />,
    iconClass: 'icon-bm',
    label: 'Saved Articles',
    delay: '0.26s',
    description: 'All the articles you bookmarked across sessions — saved locally, available offline instantly.',
    cta: 'View Bookmarks',
    tag: 'Local',
    tagClass: 'tag-warning',
    features: ['Offline storage', 'Emotion insights', 'Search & filter'],
  },
  {
    key: 'articles',
    route: '/articles',
    icon: <MdArticle />,
    iconClass: 'icon-art',
    label: 'Article Library',
    delay: '0.34s',
    description: 'Browse all published articles in the database. Filter, search, and manage your content library.',
    cta: 'Browse Articles',
    tag: 'Database',
    tagClass: 'tag-info',
    features: ['Full article list', 'Status filter', 'Author view'],
  },
];

const OverviewPage = () => {
  const history = useHistory();
  
  // Real Data States
  const [liveArticlesCount, setLiveArticlesCount] = useState('...');
  const [bookmarksCount, setBookmarksCount] = useState(0);

  useEffect(() => {
    // 1. Fetch live articles count from the API (general headlines)
    const fetchLiveArticles = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        const res = await axios.get(`${API_URL}/news?q=world&pageSize=1`);
        if (res.data && res.data.totalResults) {
          // Cap it at a realistic number or format it
          const total = res.data.totalResults > 999 ? (res.data.totalResults / 1000).toFixed(1) + 'k' : res.data.totalResults;
          setLiveArticlesCount(total);
        } else {
          setLiveArticlesCount('100+');
        }
      } catch (err) {
        setLiveArticlesCount('100+'); // Fallback
      }
    };

    // 2. Fetch bookmarks from localStorage
    const getBookmarks = () => {
      try {
        const saved = JSON.parse(localStorage.getItem('emonews-bookmarks') || '[]');
        setBookmarksCount(saved.length);
      } catch {
        setBookmarksCount(0);
      }
    };

    fetchLiveArticles();
    getBookmarks();

    // Listen for storage changes to update bookmarks live
    window.addEventListener('storage', getBookmarks);
    return () => window.removeEventListener('storage', getBookmarks);
  }, []);

  const STATS = [
    { label: 'Emotions Tracked', value: '5', icon: '🧠', color: 'var(--accent-primary)' },
    { label: 'Live Articles', value: liveArticlesCount, icon: '📰', color: 'var(--accent-success)' },
    { label: 'Languages', value: '2', icon: '🌐', color: 'var(--emotion-surprise)' },
    { label: 'Saved Articles', value: bookmarksCount.toString(), icon: '🔖', color: 'var(--accent-warning)' },
  ];

  return (
    <div className="overview-page" id="overview">
      <div className="welcome-container">
        {/* Header */}
        <header className="welcome-header animate-fade-in">
          <div className="welcome-header__left">
            <span className="welcome-header__greeting">Good day <span role="img" aria-label="waving hand">👋</span></span>
            <h1 className="welcome-title">Welcome to <span className="welcome-title__gradient">EmoNews</span></h1>
            <p className="welcome-subtitle">
              Your AI-powered news hub. Emotion detection meets contextual journalism.
            </p>
          </div>
          <div className="welcome-header__right">
            <div className="welcome-header__badge">
              <span className="badge-dot" aria-hidden="true" />
              AI Active
            </div>
          </div>
        </header>

        {/* Stats Row */}
        <div className="stats-row animate-fade-in" style={{ animationDelay: '0.05s' }}>
          {STATS.map(stat => (
            <div key={stat.label} className="stat-card">
              <span className="stat-card__icon" role="img" aria-label={stat.label}>{stat.icon}</span>
              <span className="stat-card__value" style={{ color: stat.color }}>{stat.value}</span>
              <span className="stat-card__label">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Action Cards */}
        <div className="selection-grid">
          {ACTION_CARDS.map(card => (
            <div
              key={card.key}
              className="action-card animate-fade-in"
              style={{ animationDelay: card.delay }}
              onClick={() => history.push(card.route)}
              role="button"
              tabIndex={0}
              aria-label={`Go to ${card.label}`}
              onKeyPress={e => e.key === 'Enter' && history.push(card.route)}
            >
              {/* Tag */}
              <span className={`action-card__tag ${card.tagClass}`}>{card.tag}</span>

              <div className={`action-card__icon ${card.iconClass}`} aria-hidden="true">
                {card.icon}
              </div>

              <div className="action-card__content">
                <h3>{card.label}</h3>
                <p>{card.description}</p>

                <ul className="action-card__features" aria-label={`Features of ${card.label}`}>
                  {card.features.map(f => (
                    <li key={f}>
                      <span aria-hidden="true">✓</span> {f}
                    </li>
                  ))}
                </ul>

                <div className="action-card__footer">
                  <span>{card.cta}</span>
                  <MdArrowForward aria-hidden="true" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;
